import BaseLayer from './BaseLayer';
import { PARCEL_LAYERS_MIN_ZOOM } from '../core/constants';
import { ownershipAttribution } from './attribution';
import { OWNERSHIP_COLORS } from '../core/map-legend-definitions';

export const OWNERSHIP_COLOR_EXPRESSION = [
    'match', ['get', 'grupa_rejestrowa'],
    ...OWNERSHIP_COLORS.slice(0, 16).flatMap((color, index) => [index + 1, color]),
    OWNERSHIP_COLORS[16]
];

export default class OwnershipLayer extends BaseLayer {
    static ID = 'ownership';
    static DESCRIPTION = 'Mapa własności';
    static MIN_ZOOM = PARCEL_LAYERS_MIN_ZOOM;
    static SOURCE_ID = 'src-ownership';
    static LAYER_IDS = ['lyr-ownership-fill', 'lyr-ownership-line'];

    constructor(config = {}) {
        super(config);
        this.activeSourceId = null;
    }

    getSourceId() {
        return this.constructor.SOURCE_ID;
    }

    getLayerIds() {
        return this.constructor.LAYER_IDS;
    }

    buildTileUrl() {
        const relativeUrl = (this.config.baseUrl || '') + 'api/dzialki-mvt/{z}/{x}/{y}.pbf';
        if (typeof window !== 'undefined' && window.location) {
            const origin = window.location.origin.replace(/\/$/, '');
            return origin + (relativeUrl.charAt(0) === '/' ? '' : '/') + relativeUrl;
        }
        return relativeUrl;
    }

    getParcelSourceId() {
        const controller = this.config.getParcelController ? this.config.getParcelController() : null;
        return controller && controller.getSourceId ? controller.getSourceId() : 'src-dzialki';
    }

    isParcelSourceExpected() {
        const controller = this.config.getParcelController ? this.config.getParcelController() : null;
        return !!(controller && controller.isActive() && controller.canActivate());
    }

    removeOwnershipLayers(map) {
        this.getLayerIds().forEach((layerId) => {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
        });
    }

    attachToSource(map, sourceId) {
        const sourceConfig = map.getStyle().sources[sourceId];
        if (!sourceConfig) return false;

        this.removeOwnershipLayers(map);
        if (this.activeSourceId === this.getSourceId()
            && sourceId !== this.getSourceId()
            && map.getSource(this.getSourceId())) {
            map.removeSource(this.getSourceId());
        }

        const vectorLayer = sourceConfig.type === 'vector' ? { 'source-layer': 'dzialki' } : {};
        map.addLayer(Object.assign({
            id: this.getLayerIds()[0],
            type: 'fill',
            source: sourceId,
            minzoom: this.minZoom,
            paint: {
                'fill-color': this.getFillColorExpression(),
                'fill-opacity': this.getFillOpacity()
            }
        }, vectorLayer));
        map.addLayer(Object.assign({
            id: this.getLayerIds()[1],
            type: 'line',
            source: sourceId,
            minzoom: this.minZoom,
            paint: {
                'line-color': this.getLineColor(),
                'line-width': 0.7
            }
        }, vectorLayer));

        this.activeSourceId = sourceId;
        if (this.config.setLayer) this.config.setLayer(sourceId);
        if (this.config.onLoaded) this.config.onLoaded();
        return true;
    }

    getFillColorExpression() {
        return OWNERSHIP_COLOR_EXPRESSION;
    }

    getFillOpacity() {
        return 0.42;
    }

    getLineColor() {
        return 'rgba(87,65,47,0.42)';
    }

    getAttribution() {
        return ownershipAttribution();
    }

    syncSharedSource() {
        if (!this.isActive() || !this.canActivate()) return false;
        const map = this.getMap();
        const sourceId = this.getParcelSourceId();
        return !!map && !!map.getSource(sourceId) && this.attachToSource(map, sourceId);
    }

    load() {
        if (!this.isActive() || !this.canActivate()) {
            this.clear();
            return;
        }

        const map = this.getMap();
        if (!map) return;

        if (this.syncSharedSource()) return;
        if (this.isParcelSourceExpected()) return;

        const sourceId = this.getSourceId();
        if (!map.getSource(sourceId)) {
            map.addSource(sourceId, {
                type: 'vector',
                tiles: [this.buildTileUrl()],
                maxzoom: this.config.maxZoom || 22,
                attribution: this.getAttribution()
            });
        }
        this.attachToSource(map, sourceId);
    }

    clear() {
        const map = this.getMap();
        if (map) {
            this.removeOwnershipLayers(map);
            if (map.getSource(this.getSourceId())) {
                map.removeSource(this.getSourceId());
            }
        }
        this.activeSourceId = null;
        if (this.config.setLayer) this.config.setLayer(null);
        if (this.config.onCleared) this.config.onCleared();
    }
}
